/**
 * useTagNameLookup Hook
 *
 * Provides tag ID to name mapping functionality.
 * Eliminates duplicate code across WorkPage, LabPage, WorkDetailPage, LabDetailPage.
 */

import { useMemo, useCallback } from 'react';
import type { Tag } from '../types';

// =============================================================================
// TYPES
// =============================================================================

export interface UseTagNameLookupReturn {
  /** Map from tag ID to tag object */
  tagMap: Map<string, Tag>;
  /** Map from tag ID to tag name only */
  tagNameMap: Map<string, string>;
  /** Get tag names for an array of tag IDs */
  getTagNames: (tagIds: string[]) => string[];
  /** Get a single tag name by ID */
  getTagName: (tagId: string) => string;
  /** Get a tag object by ID */
  getTag: (tagId: string) => Tag | undefined;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for looking up tag names from tag IDs
 *
 * @param tags - Array of Tag objects
 * @returns Object with lookup utilities
 *
 * @example
 * ```tsx
 * const { tags: allTags } = useTags();
 * const { getTagNames } = useTagNameLookup(allTags);
 *
 * // In render
 * {getTagNames(post.tags).map(name => <span key={name}>{name}</span>)}
 * ```
 */
export function useTagNameLookup(tags: Tag[]): UseTagNameLookupReturn {
  // Full tag map (ID -> Tag object)
  const tagMap = useMemo(() => {
    const map = new Map<string, Tag>();
    tags.forEach((tag) => map.set(tag.id, tag));
    return map;
  }, [tags]);

  // Name-only map (ID -> name string)
  const tagNameMap = useMemo(() => {
    const map = new Map<string, string>();
    tags.forEach((tag) => map.set(tag.id, tag.name));
    return map;
  }, [tags]);

  // Get multiple tag names
  const getTagNames = useCallback(
    (tagIds: string[]): string[] => {
      return tagIds.map((id) => tagNameMap.get(id) || id);
    },
    [tagNameMap]
  );

  // Get single tag name
  const getTagName = useCallback(
    (tagId: string): string => {
      return tagNameMap.get(tagId) || tagId;
    },
    [tagNameMap]
  );

  // Get full tag object
  const getTag = useCallback(
    (tagId: string): Tag | undefined => {
      return tagMap.get(tagId);
    },
    [tagMap]
  );

  return {
    tagMap,
    tagNameMap,
    getTagNames,
    getTagName,
    getTag,
  };
}

// =============================================================================
// ADDITIONAL UTILITIES
// =============================================================================

export interface UseFilteredTagsReturn {
  /** Tags that are actually used by the provided posts */
  usedTags: Tag[];
  /** Tag IDs that are used */
  usedTagIds: Set<string>;
}

/**
 * Hook for getting tags that are actually used by a set of posts
 *
 * @param allTags - All available tags
 * @param posts - Posts to check for tag usage
 * @returns Object with filtered tags
 *
 * @example
 * ```tsx
 * const { tags: allTags } = useTags();
 * const { posts } = usePosts({ type: 'work' });
 * const { usedTags } = useFilteredTags(allTags, posts);
 * ```
 */
export function useFilteredTags(
  allTags: Tag[],
  posts: Array<{ tags: string[] }>
): UseFilteredTagsReturn {
  const usedTagIds = useMemo(() => {
    const ids = new Set<string>();
    posts.forEach((post) => {
      post.tags.forEach((tagId) => ids.add(tagId));
    });
    return ids;
  }, [posts]);

  const usedTags = useMemo(() => {
    return allTags.filter((tag) => usedTagIds.has(tag.id));
  }, [allTags, usedTagIds]);

  return { usedTags, usedTagIds };
}

// =============================================================================
// POST FILTERING BY TAGS
// =============================================================================

export interface UsePostTagFilterReturn<T> {
  /** Filtered posts based on selected tags */
  filteredPosts: T[];
  /** Check if a post matches the selected tags */
  matchesTags: (post: T) => boolean;
}

/**
 * Hook for filtering posts by selected tags
 *
 * @param posts - Posts to filter
 * @param selectedTags - Array of selected tag IDs
 * @returns Object with filtered posts
 *
 * @example
 * ```tsx
 * const [selectedTags, setSelectedTags] = useState<string[]>([]);
 * const { filteredPosts } = usePostTagFilter(posts, selectedTags);
 * ```
 */
export function usePostTagFilter<T extends { tags: string[] }>(
  posts: T[],
  selectedTags: string[]
): UsePostTagFilterReturn<T> {
  const matchesTags = useCallback(
    (post: T): boolean => {
      if (selectedTags.length === 0) return true;
      return selectedTags.every((tagId) => post.tags.includes(tagId));
    },
    [selectedTags]
  );

  const filteredPosts = useMemo(() => {
    if (selectedTags.length === 0) return posts;
    return posts.filter(matchesTags);
  }, [posts, selectedTags, matchesTags]);

  return { filteredPosts, matchesTags };
}
